import { generatePath, useParams } from "react-router-dom";
import MachineCard from "@/machines/components/common/machine-card/machine-card.tsx";
import MachineCardSkeleton from "@/machines/components/common/machine-card/machine-card-skeleton.tsx";
import { MapView } from "@/map";
import PageHeader from "@/shared/components/layout/dashboard-layout/page-header.tsx";
import PageContent from "@/shared/components/layout/dashboard-layout/page-content.tsx";
import SkeletonPage from "@/shared/components/skeleton/page/skeleton-page.tsx";
import EmptyState from "@/shared/components/ui/empty-state/empty-state.tsx";
import MachinesLiveMain from "@/machines/components/common/machines-live/machines-live-main.tsx";
import MachinesLiveSkeleton from "@/machines/components/common/machines-live/machines-live-skeleton.tsx";
// import MachinesLiveEmpty from "@/machines/components/common/machines-live/machines-live-empty.tsx";
import { useDateTimeRangeStore } from "@/shared/stores/useDateTimeRangeStore.ts";
import { useGetTruckEventsOverview } from "@/machines/api/queries/events.queries.ts";
import { useLocationDetailCoordinates } from "@/locations/hooks/use-location-detail-coordinates";
import { useOrganizationStore } from "@/shared/stores/useOrganizationStore.ts";
import { useGetOrganizationById } from "@/auth/api/queries/user.queries.ts";
import { useGetLocationById } from "@/locations/api/queries/location.queries.ts";
import { MACHINES_URL } from "@/machines/routes.ts";
import KPIStripe from "@/machines/components/location/kpi-stripe.tsx";
import { GiResize } from "react-icons/gi";
import { LocationDetailResizeProvider, useLocationDetailResize } from "@/locations/contexts/location-detail-resize-context.tsx";
import { Machine } from "@/machines/types/machine.ts";
import { useSetBreadcrumbs } from "@/shared/contexts/breadcrumb-context";
import { useLocationMachinesOrder } from "@/locations/hooks/use-location-machines-order.ts";
import AggregatedAlertsTable from "@/machines/components/table/aggregated-alerts-table.tsx";
import { useTranslation } from "react-i18next";

const LocationDetail = () => {
  return (
    <LocationDetailResizeProvider>
      <LocationDetailContent />
    </LocationDetailResizeProvider>
  );
};

const LocationDetailContent = () => {
  const { t } = useTranslation("locations");
  const { id } = useParams();
  const { selectedOrganizationId } = useOrganizationStore();
  const { expandedComponent, toggleMapExpanded } = useLocationDetailResize();

  const locationId = id ? parseInt(id) : 0;
  const { data: orgData, isLoading: isOrgLoading } = useGetOrganizationById(selectedOrganizationId!);
  const { data: locationData, isLoading: isLocationLoading } = useGetLocationById(locationId);

  const machines = locationData?.data?.machines;
  const machinesLoading = isLocationLoading;

  const { basicCoordinates } = useLocationDetailCoordinates(locationData?.data?.machines, null);

  const isMapExpanded = expandedComponent === "map";
  const isMachineExpanded = expandedComponent !== "none" && expandedComponent !== "map";

  // Set breadcrumbs using the hook - they will appear in the top-bar
  useSetBreadcrumbs([
    {
      path: MACHINES_URL.LOCATION,
      breadcrumb: orgData?.data?.name || t("breadcrumbs.organization"),
      type: "organizations",
      currentId: selectedOrganizationId!,
      isLoading: isOrgLoading
    },
    {
      path: generatePath(MACHINES_URL.LOCATION_DETAIL, { id: id?.toString() ?? "" }),
      breadcrumb: locationData?.data?.name || t("breadcrumbs.location", { id }),
      type: "locations",
      currentId: locationId,
      isLoading: isLocationLoading
    },
  ]);

  const { orderedMachines, getDraggableProps } = useLocationMachinesOrder(locationId, machines);

  // Collect all device_mxids from machines for aggregated alerts
  const deviceMxids = orderedMachines.map((machine) => machine.device_mxid).filter(Boolean);

  if (machinesLoading) {
    return <SkeletonPage />
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader />

      {/* KPI Stripe */}
      <div className="flex-shrink-0">
        <KPIStripe />
      </div>

      <div className="flex-1">
        <PageContent>
          {/* When map is expanded - Full width map on top, machines below */}
          {isMapExpanded ? (
            <div className="flex flex-col gap-6 h-full">
              <div className="w-full rounded-2xl p-4 bg-sidebar relative">
                <button
                  onClick={toggleMapExpanded}
                  className="absolute top-6 right-6 z-10 bg-background/80 hover:bg-background/90 p-2 rounded-lg transition-all hover:opacity-70"
                >
                  <GiResize />
                </button>
                <MapView
                  key={`location-detail-map-${id}`}
                  basicCoordinates={basicCoordinates}
                  options={{
                    defaultZoom: 14,
                    zoomControl: false,
                  }}
                  resizable={true}
                  initialHeight={600}
                  storageKey={"location-detail-map-height"}
                  isLoading={machinesLoading}
                  emptyStateTitle={t("detail.map.emptyTitle")}
                  emptyStateDescription={t("detail.map.emptyDescription")}
                  loadingText={t("detail.map.loading")}
                  locationId={id ? parseInt(id) : undefined}
                  showPolygonControls={true}
                />
              </div>

              {/* Aggregated Alerts Table */}
              <AggregatedAlertsTable device_mxids={deviceMxids} shouldModalOpen={true} />

              {/* Machine Cards Below */}
              <div className="flex flex-col gap-4 overflow-y-auto">
                {machinesLoading ? (
                  Array.from({ length: 4 }).map((_, index) => (
                    <MachineCardSkeleton key={index} />
                  ))
                ) : orderedMachines && orderedMachines.length > 0 ? (
                  orderedMachines.map((machine) => (
                    <div
                      key={machine.id}
                      {...getDraggableProps(String(machine.id))}
                    >
                      <MachineCardWithChart machine={machine} />
                    </div>
                  ))
                ) : (
                  <EmptyState
                    title={t("detail.empty.title")}
                    description={t("detail.empty.description")}
                  />
                )}
              </div>

              {/* Notifications and Alerts Section */}
             
            </div>
          ) : (
            /* Normal or Machine Expanded Layout */
            <div className="grid grid-cols-10 gap-6 h-full">
              {/* Left Column - Machine Cards (70% - 7 columns) */}
              <div className={`${isMachineExpanded ? 'col-span-10' : 'col-span-7'} flex flex-col gap-4 overflow-y-auto`}>
                {machinesLoading ? (
                  // Show skeleton cards while loading
                  Array.from({ length: 4 }).map((_, index) => (
                    <MachineCardSkeleton key={index} />
                  ))
                ) : machines && machines.length > 0 ? (
              // Show actual machine cards with live preview
              orderedMachines.map((machine) => (
                <div
                  key={machine.id}
                  {...getDraggableProps(String(machine.id))}
                >
                  <MachineCardWithChart machine={machine} />
                </div>
              ))
                ) : (
                  // Show empty state when no machines
                  <EmptyState
                    title={t("detail.empty.title")}
                    description={t("detail.empty.description")}
                  />
                )}
                {/* When a machine is expanded, render Map and Alerts below charts */}
                {isMachineExpanded && (
                  <div className="flex flex-col gap-6">
                    <div className="w-full rounded-2xl p-4 bg-sidebar relative">
                      <button
                        onClick={toggleMapExpanded}
                        className="absolute top-6 right-6 z-10 bg-background/80 hover:bg-background/90 p-2 rounded-lg transition-all hover:opacity-70"
                      >
                        <GiResize />
                      </button>
                      <MapView
                        key={`location-detail-map-${id}`}
                        basicCoordinates={basicCoordinates}
                        options={{
                          defaultZoom: 14,
                          zoomControl: false,
                        }}
                        resizable={true}
                        initialHeight={600}
                        storageKey={"location-detail-map-height"}
                        isLoading={machinesLoading}
                        emptyStateTitle={t("detail.map.emptyTitle")}
                        emptyStateDescription={t("detail.map.emptyDescription")}
                        loadingText={t("detail.map.loading")}
                        locationId={id ? parseInt(id) : undefined}
                        showPolygonControls={true}
                      />
                    </div>

                    <AggregatedAlertsTable device_mxids={deviceMxids} shouldModalOpen={true} />
                  </div>
                )}
              </div>

              {/* Right Column - Map and Alerts/Notifications (30% - 3 columns) */}
              {!isMachineExpanded && (
                <div className="col-span-3 flex flex-col gap-6 overflow-y-auto">
            {/* Map Section */}
            <div className="w-full rounded-2xl p-4 bg-sidebar relative">
              <button
                onClick={toggleMapExpanded}
                className="absolute top-6 right-6 z-10 bg-background/80 hover:bg-background/90 p-2 rounded-lg transition-all hover:opacity-70"
              >
                <GiResize />
              </button>
              <MapView
                key={`location-detail-map-${id}`}
                basicCoordinates={basicCoordinates}
                options={{
                  defaultZoom: 14,
                  zoomControl: false,
                }}
                resizable={true}
                initialHeight={600}
                storageKey={"location-detail-map-height"}
                isLoading={machinesLoading}
                emptyStateTitle={t("detail.map.emptyTitle")}
                emptyStateDescription={t("detail.map.emptyDescription")}
                loadingText={t("detail.map.loading")}
                locationId={id ? parseInt(id) : undefined}
                showPolygonControls={true}
              />
            </div>

            {/* Aggregated Alerts Table */}
            <AggregatedAlertsTable device_mxids={deviceMxids} shouldModalOpen={true} />
                </div>
              )}
            </div>
          )}
        </PageContent>
      </div>
    </div>
  )
}

interface MachineCardWithChartProps {
  machine: Machine;
}

const MachineCardWithChart = ({ machine }: MachineCardWithChartProps) => {
  const { t } = useTranslation("locations");
  const { value } = useDateTimeRangeStore();
  const { expandedComponent, toggleMachineExpanded } = useLocationDetailResize();
  const { data: overview, status } = useGetTruckEventsOverview({
    device_mxid: machine.device_mxid,
    start_time: value.fromDate,
    end_time: value.toDate,
    page: 1,
    itemsPerPage: 3000,
  });


  const isLoading = status === "pending";
  const isEmpty = !isLoading && (!overview?.data || !overview.data.graphData || overview.data.graphData.length === 0 || !overview.data.metadata);
  const isExpanded = expandedComponent === machine.id.toString();

  return (
    <div className="rounded-2xl relative bg-sidebar border-t-4 p-6 flex gap-4" style={{ borderColor: machine.color }}>
      {/* Left Side - Machine Card Info */}
      <div className="flex w-full max-w-[250px] ">
        <MachineCard
          machine={machine}
          metadata={overview?.data?.metadata}
          isKpiLoading={isLoading}
        />
      </div>

      {/* Right Side - Live Preview Chart */}
      <div className={`flex-1 flex items-center justify-center ${isExpanded ? 'min-h-[400px]' : 'min-h-[200px]'}`}>
        {isLoading ? (
          <MachinesLiveSkeleton />
        ) : isEmpty ? (
          <div className="text-center text-sm text-muted-foreground">
            {t("detail.noData")}
          </div>
        ) : (
          <div className="w-full">
            <MachinesLiveMain
              metadata={overview!.data!.metadata}
              chartData={overview!.data!.graphData}
              width={isExpanded ? 1200 : 700}
              height={isExpanded ? 400 : 200}
            />
          </div>
        )}
      </div>
      <button
        className={"absolute top-2 right-2 hover:opacity-70 transition-opacity"}
        onClick={() => toggleMachineExpanded(machine.id.toString())}
      >
        <GiResize />
      </button>
    </div>
  );
};

export default LocationDetail;